import { z } from 'zod';
import { WebhookDeliveryStatusSchema, WebhookEventTypeSchema } from '../common/index.js';
const RetryPolicySchema = z.object({
    max_retries: z.number().int().min(0).max(10).default(3),
    retry_delay_seconds: z.number().int().min(1).max(3600).default(60),
    backoff_multiplier: z.number().min(1).max(10).default(2),
});
const RateLimitSchema = z.object({
    requests_per_minute: z.number().int().min(1).max(1000).default(60),
});
export const WebhookAgentFilterSchema = z.object({
    agent_id: z.string(),
    action_ids: z.array(z.string()).optional(),
});
export const WebhookFiltersSchema = z.object({
    agents: z.array(WebhookAgentFilterSchema).optional(),
});
export const CreateWebhookSchema = z.object({
    url: z.string().url({ message: 'Must be a valid HTTPS URL' }),
    events: z.array(WebhookEventTypeSchema).min(1),
    description: z.string().max(500).optional(),
    secret: z.string().min(16).max(128).optional(),
    headers: z.record(z.string(), z.string()).optional(),
    filters: WebhookFiltersSchema.optional(),
    retry_policy: RetryPolicySchema.optional(),
    rate_limit: RateLimitSchema.optional(),
    active: z.boolean().optional().default(true),
});
export const UpdateWebhookSchema = z.object({
    url: z.string().url().optional(),
    events: z.array(WebhookEventTypeSchema).min(1).optional(),
    description: z.string().max(500).optional(),
    secret: z.string().min(16).max(128).optional(),
    headers: z.record(z.string(), z.string()).optional(),
    filters: WebhookFiltersSchema.optional(),
    retry_policy: RetryPolicySchema.optional(),
    rate_limit: RateLimitSchema.optional(),
    active: z.boolean().optional(),
});
export const TestWebhookSchema = z.object({
    event_type: WebhookEventTypeSchema.optional(),
    test_payload: z.record(z.string(), z.unknown()).optional(),
});
export const WebhookParamsSchema = z.object({
    id: z.string(),
});
export const RetryDeliveryParamsSchema = z.object({
    id: z.string(),
    delivery_id: z.string(),
});
export const WebhookListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20).optional(),
    active: z.coerce.boolean().optional(),
    event_type: z.string().optional(),
    url_contains: z.string().optional(),
});
export const WebhookDeliveriesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20).optional(),
    status: WebhookDeliveryStatusSchema.optional(),
    event_type: z.string().optional(),
    start_date: z.string().datetime().optional(),
    end_date: z.string().datetime().optional(),
});
export const WebhookResponseSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    url: z.string().url(),
    events: z.array(z.string()),
    description: z.string().nullable(),
    secret: z.string().nullable(),
    headers: z.record(z.string(), z.string()),
    filters: WebhookFiltersSchema.nullable(),
    retry_policy: z.object({
        max_retries: z.number(),
        retry_delay_seconds: z.number(),
        backoff_multiplier: z.number(),
    }),
    rate_limit: z.object({
        requests_per_minute: z.number(),
    }),
    active: z.boolean(),
    stats: z.object({
        total_deliveries: z.number(),
        successful_deliveries: z.number(),
        failed_deliveries: z.number(),
        last_delivery_at: z.string().datetime().nullable(),
        last_success_at: z.string().datetime().nullable(),
        avg_response_time_ms: z.number(),
    }),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const PaginatedWebhooksResponseSchema = z.object({
    data: z.array(WebhookResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
    }),
});
export const WebhookDeliverySchema = z.object({
    id: z.string(),
    webhook_id: z.string(),
    event_type: z.string(),
    status: WebhookDeliveryStatusSchema,
    http_status: z.number().nullable(),
    response_time_ms: z.number().nullable(),
    error_message: z.string().nullable(),
    retry_count: z.number(),
    next_retry_at: z.string().datetime().nullable(),
    payload_size_bytes: z.number(),
    created_at: z.string().datetime(),
    delivered_at: z.string().datetime().nullable(),
});
export const WebhookDeliveriesResponseSchema = z.object({
    deliveries: z.array(WebhookDeliverySchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
    }),
});
export const TestWebhookResponseSchema = z.object({
    delivery_id: z.string(),
    status: z.string(),
    http_status: z.number().nullable(),
    response_time_ms: z.number().nullable(),
    error_message: z.string().nullable(),
    sent_at: z.string().datetime(),
});
export const RetryDeliveryResponseSchema = z.object({
    delivery_id: z.string(),
    retry_count: z.number(),
    status: z.string(),
    scheduled_at: z.string().datetime(),
});
export const WebhookPayloadSchema = z.object({
    id: z.string(),
    type: z.string(),
    data: z.record(z.string(), z.unknown()),
    timestamp: z.string().datetime(),
    tenant_id: z.string(),
    version: z.string().optional().default('1.0'),
});
//# sourceMappingURL=index.js.map